import { useTranslation } from "react-i18next";
import { Divider } from "semantic-ui-react";

import { useCan } from "components";
import { createFormHelper } from "lib/Form";
import { DEALER_REPORT_COLUMNSS } from "models";
import "modules/DealerOverviewDashboard/components/DmsSystem/components/ThresholdSettings/ThresholdSettings.scss";

import type { DefaultReportThreshold } from "modules/DealerOverviewDashboard/components/DmsSystem/components/ThresholdSettings/hooks";
import type { DealerOverviewFormPayload } from "modules/DealerOverviewDashboard/hooks";

const FormHelper = createFormHelper<DealerOverviewFormPayload>();

const THRESHOLD_LABELS: Partial<Record<DEALER_REPORT_COLUMNSS, { key: string; fallback: string }>> = {
  [DEALER_REPORT_COLUMNSS.WOHandled]: { key: "v8_wo_handled", fallback: "WO Handled" },
  [DEALER_REPORT_COLUMNSS.CheckAanname]: { key: "v8_initial_checks", fallback: "Initial Checks" },
  [DEALER_REPORT_COLUMNSS.CheckCommercial]: { key: "v8_commercial_checks", fallback: "Commercial Checks" },
  [DEALER_REPORT_COLUMNSS.CheckMedia]: { key: "v8_photos_videos", fallback: "Photos / Videos" },
  [DEALER_REPORT_COLUMNSS.CheckCount]: { key: "v8_checks_count", fallback: "Checks Count" },
  [DEALER_REPORT_COLUMNSS.StatusQuality]: { key: "v8_quality_control", fallback: "Quality Control" },
  [DEALER_REPORT_COLUMNSS.CCExtraServices]: { key: "v8_extra_services", fallback: "Extra Services" }
};

const COLUMN_SIZES = [3, 2, 2];

export const isThresholdInvalid = ({ low_value, high_value }: Partial<DefaultReportThreshold> = {}) => low_value != null && high_value != null && low_value > high_value;

export const ThresholdSettings = () => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();

  const canUpdateThresholds = useCan("update", "dealers-report-threshold");
  const thresholds = watch("dealer_report_thresholds");

  if (!thresholds.length) return null;

  const thresholdColumns = COLUMN_SIZES.map((size, columnIndex) => {
    const start = COLUMN_SIZES.slice(0, columnIndex).reduce((sum, columnSize) => sum + columnSize, 0);
    return thresholds.slice(start, start + size).map((threshold, offset) => ({ threshold, index: start + offset }));
  });

  return (
    <div className="ThresholdSettings">
      <div className="dealer-overview-subheading">{t("v8_threshold_settings", "Threshold settings")}</div>
      <div className="threshold-columns">
        {thresholdColumns.map(column => (
          <div className="threshold-column" key={column[0]?.threshold.dealer_report_column}>
            {column.map(({ threshold, index }) => {
              const label = THRESHOLD_LABELS[threshold.dealer_report_column];

              return (
                <div className="threshold-row" key={threshold.dealer_report_column}>
                  <div className="threshold-inputs">
                    <FormHelper.Field.Input
                      name={`dealer_report_thresholds.${index}.low_value`}
                      nullable
                      inputProps={{ type: "number", className: "threshold-input-low", disabled: !canUpdateThresholds }}
                    />
                    <FormHelper.Field.Input
                      name={`dealer_report_thresholds.${index}.high_value`}
                      nullable
                      inputProps={{ type: "number", className: "threshold-input-high", disabled: !canUpdateThresholds }}
                    />
                  </div>
                  <div>
                    <label className={`threshold-label${isThresholdInvalid(threshold) ? " threshold-label-error" : ""}`}>
                      {label ? t(label.key, label.fallback) : ""}
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <Divider />
    </div>
  );
};
